import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, TIPOS, OPC } from "@/lib/api";

export const NewTreatmentDialog = ({ tipo: fixedTipo, patientId, onCreated, triggerLabel }) => {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState(fixedTipo || "bono");
  const [q, setQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [pid, setPid] = useState(patientId || "");
  const [modalidad, setModalidad] = useState("BONO 14 SESIONES");
  const [modSes, setModSes] = useState("SESIÓN QUINCENAL");
  const [nSes, setNSes] = useState("");
  const completo = tipo === "bono" && modalidad === "BONO TRATAMIENTO COMPLETO";
  const nav = useNavigate();

  useEffect(() => {
    if (!open || patientId) return;
    api.get("/patients", { params: { q } }).then((r) => setPatients(r.data.slice(0, 50)));
  }, [open, q, patientId]);

  const submit = async () => {
    if (!pid) return toast.error("Selecciona un paciente");
    if (completo && !(Number(nSes) > 0)) return toast.error("Escribe cuántas sesiones se han vendido");
    const { data } = await api.post("/treatments", { patient_id: pid, tipo, estado: "pendiente", ...(tipo === "bono" ? { modalidad, ...(completo ? { sesiones_presupuestadas: Number(nSes) } : {}) } : tipo === "sesiones" ? { modalidad: modSes } : {}) });
    toast.success(`${TIPOS[tipo].single} creado`);
    setOpen(false);
    onCreated ? onCreated(data) : nav(`/tratamiento/${data.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-treatment-btn" variant="outline" className="h-9"><Plus className="h-4 w-4 mr-1" />{triggerLabel || "Nuevo tratamiento"}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="new-treatment-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Nuevo tratamiento</DialogTitle></DialogHeader>
        {!fixedTipo && (
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(TIPOS).map(([k, t]) => (
              <button key={k} type="button" data-testid={`nt-tipo-${k}`} onClick={() => setTipo(k)}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${tipo === k ? t.cls + " ring-2 ring-offset-1 ring-slate-300" : "border-slate-200 hover:bg-slate-50"}`}>
                <t.icon className="h-4 w-4" />{t.single}
              </button>
            ))}
          </div>
        )}
        {tipo === "bono" && (
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo de bono
            <select data-testid="nt-modalidad" className="field-select mt-1" value={modalidad} onChange={(e) => setModalidad(e.target.value)}>
              {OPC.modalidad.map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
        )}
        {completo && (
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Sesiones vendidas
            <Input data-testid="nt-sesiones" type="number" min={1} className="mt-1" placeholder="p. ej. 24" value={nSes} onChange={(e) => setNSes(e.target.value)} />
          </label>
        )}
        {tipo === "sesiones" && (
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo de sesión
            <select data-testid="nt-modalidad-ses" className="field-select mt-1" value={modSes} onChange={(e) => setModSes(e.target.value)}>
              {OPC.modalidad_ses.map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
        )}
        {!patientId && (
          <div className="space-y-2">
            <Input data-testid="nt-search" placeholder="Buscar paciente…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-60 overflow-y-auto border rounded-md divide-y">
              {patients.map((p) => (
                <button key={p.id} type="button" data-testid={`nt-patient-${p.id}`} onClick={() => setPid(p.id)}
                  className={`w-full text-left px-3 py-2 text-sm ${pid === p.id ? "bg-indigo-50 text-indigo-800" : "hover:bg-slate-50"}`}>
                  {p.nombre} <span className="text-xs text-slate-400">{p.centro}</span>
                </button>
              ))}
              {patients.length === 0 && <p className="p-3 text-sm text-slate-400">Sin resultados</p>}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button data-testid="nt-submit" onClick={submit} className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Crear</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
